import React from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { useField, useFormikContext } from "formik";
import * as yup from "yup";

const REASONS = [
  "I do not plan on using the zones again",
  "I put in too much money",
  "The auto-refill amount is too much",
  "I don’t want to keep money in my account",
  "Other",
];

const WalletRefundRequestReasonDropdownFieldPrivate = <DisableClearable extends boolean | undefined>({
  name = "reason",
  label = "Feedback",
  placeholder = "Feedback",
  disablePortal = true,
  fullWidth = true,
  autoSelect = false,
  autoHighlight = true,
  openOnFocus = true,
  required = false,
  helperText,
  filterOptions = (options: string[]) => options,
  disabled,
  ...rest
}: WalletRefundRequestReasonDropdownFieldProps<DisableClearable>) => {
  const { isSubmitting } = useFormikContext();
  const [field, meta, { setValue }] = useField<DisableClearable extends true ? string : string | null>(name);
  const [otherField, otherMeta, { setValue: setOtherValue }] = useField<string | null>("other");

  const showError = meta.touched && !!meta.error;
  const showOtherError = otherMeta.touched && !!otherMeta.error;

  return (
    <Box>
      <Autocomplete<string, false, DisableClearable, false>
        {...rest}
        {...{ disablePortal, fullWidth, autoSelect, autoHighlight, openOnFocus }}
        multiple={false}
        onChange={(e, v) => {
          setValue(v);
          setOtherValue("");
        }}
        getOptionLabel={(option) => `${option}`}
        options={REASONS}
        value={meta.value}
        filterOptions={filterOptions}
        onBlur={field.onBlur}
        disabled={disabled ?? isSubmitting}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            error={showError}
            helperText={showError ? meta.error : helperText}
            label={label}
            placeholder={placeholder}
            required={required}
          />
        )}
      />
      {meta.value === "Other" && (
        <TextField
          sx={{ mt: { xs: 3.5, tiny: 4 }, width: "100%" }}
          value={otherMeta.value}
          onChange={(e) => {
            setOtherValue(e.target.value);
          }}
          name="other"
          onBlur={otherField.onBlur}
          error={showOtherError}
          helperText={showOtherError ? otherMeta.error : ""}
          label="Other"
          placeholder="Other"
          required={required}
        />
      )}
    </Box>
  );
};

/**
 * Component that renders an autocomplete dropdown allowing a user to select the
 * reason why user requests wallet refund.
 */
export const WalletRefundRequestReasonDropdownField = Object.assign(WalletRefundRequestReasonDropdownFieldPrivate, {
  defaultInitialValue: null as string | null,
  validationSchema: yup.string().nullable().required("Selection required."),
});

/** Props passed to initialize a {@link WalletRefundRequestReasonDropdownField} component */
export interface WalletRefundRequestReasonDropdownFieldProps<DisableClearable extends boolean | undefined>
  extends Omit<
    AutocompleteProps<string, false, DisableClearable, false>,
    "renderInput" | "options" | "getOptionLabel" | "onChange" | "onBlur" | "value"
  > {
  name?: string;
  required?: boolean;
  label?: string;
  helperText?: string;
}

export default WalletRefundRequestReasonDropdownField;
